import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Grip, Move } from "lucide-react";
import { cn } from "@/lib/utils";
import MapleOSLogo from "@/components/common/MapleOSLogo";
import HeroNodeDetailsDialog, { type HeroNodeId } from "@/components/landing/HeroNodeDetailsDialog";
import MapleOSScreenshotGallery from "@/components/landing/MapleOSScreenshotGallery";

const transcriptId = "hero-system-preview-transcript";
const leftRail = ["Live context", "Files", "Knowledge", "Recent work"];
const rightRail = ["Reasoning", "Predictions", "Blueprint runs", "Apps", "System actions"];
const inputDotTimings = [
  { baseDelay: "0.08s", duration: "1.55s" },
  { baseDelay: "0.52s", duration: "1.34s" },
  { baseDelay: "0.27s", duration: "1.48s" },
  { baseDelay: "0.74s", duration: "1.62s" },
] as const;

const SVG_WIDTH = 1000;
const SVG_HEIGHT = 620;

const initialCore = {
  centerX: 500,
  centerY: 260,
};

const compactCore = {
  centerX: 500,
  centerY: 310,
};

const mediumNodeLayout: Record<HeroNodeId, Pick<LayoutNode, "x" | "y" | "anchorX" | "anchorY" | "coreX" | "coreY">> = {
  chat: {
    x: 23,
    y: 20,
    anchorX: 395,
    anchorY: 106,
    coreX: 452,
    coreY: 222,
  },
  terminal: {
    x: 77,
    y: 20,
    anchorX: 605,
    anchorY: 106,
    coreX: 548,
    coreY: 222,
  },
  knowledge: {
    x: 18,
    y: 50,
    anchorX: 382,
    anchorY: 310,
    coreX: 446,
    coreY: 310,
  },
  blueprints: {
    x: 82,
    y: 50,
    anchorX: 618,
    anchorY: 310,
    coreX: 554,
    coreY: 310,
  },
  files: {
    x: 23,
    y: 80,
    anchorX: 402,
    anchorY: 514,
    coreX: 452,
    coreY: 398,
  },
  apps: {
    x: 77,
    y: 80,
    anchorX: 598,
    anchorY: 514,
    coreX: 548,
    coreY: 398,
  },
};

const mobileCore = {
  centerX: 500,
  centerY: 320,
};

const mobileNodeLayout: Record<HeroNodeId, Pick<LayoutNode, "x" | "y" | "anchorX" | "anchorY" | "coreX" | "coreY">> = {
  chat: {
    x: 24,
    y: 20,
    anchorX: 400,
    anchorY: 108,
    coreX: 455,
    coreY: 220,
  },
  terminal: {
    x: 76,
    y: 20,
    anchorX: 600,
    anchorY: 108,
    coreX: 545,
    coreY: 220,
  },
  knowledge: {
    x: 20,
    y: 49,
    anchorX: 388,
    anchorY: 320,
    coreX: 445,
    coreY: 320,
  },
  blueprints: {
    x: 80,
    y: 49,
    anchorX: 612,
    anchorY: 320,
    coreX: 555,
    coreY: 320,
  },
  files: {
    x: 24,
    y: 78,
    anchorX: 410,
    anchorY: 532,
    coreX: 455,
    coreY: 420,
  },
  apps: {
    x: 76,
    y: 78,
    anchorX: 590,
    anchorY: 532,
    coreX: 545,
    coreY: 420,
  },
};

type LayoutNode = {
  id: HeroNodeId;
  label: string;
  iconSrc: string;
  iconAlt: string;
  chip: string;
  x: number;
  y: number;
  anchorX: number;
  anchorY: number;
  coreX: number;
  coreY: number;
};

const initialNodes: LayoutNode[] = [
  {
    id: "chat",
    label: "Chat",
    iconSrc: "/os/assets/icons/ai-chat.svg",
    iconAlt: "AI Chat",
    chip: "MaplePT session",
    x: 16,
    y: 15,
    anchorX: 293.9,
    anchorY: 12.14,
    coreX: 363.3,
    coreY: 139.08,
  },
  {
    id: "terminal",
    label: "Terminal",
    iconSrc: "/os/assets/icons/terminal.svg",
    iconAlt: "Terminal",
    chip: "shell://runtime",
    x: 84,
    y: 15,
    anchorX: 701.89,
    anchorY: 12.14,
    coreX: 633.54,
    coreY: 136.49,
  },
  {
    id: "knowledge",
    label: "Knowledge",
    iconSrc: "/os/assets/icons/maple-seed-vault.svg",
    iconAlt: "Maple Seed Vault",
    chip: "memory + context",
    x: 12,
    y: 49,
    anchorX: 274.97,
    anchorY: 271.2,
    coreX: 354.89,
    coreY: 269.47,
  },
  {
    id: "blueprints",
    label: "Blueprints",
    iconSrc: "/os/assets/icons/blueprint-studio.svg",
    iconAlt: "Blueprint Studio",
    chip: "blueprint://launch",
    x: 88,
    y: 49,
    anchorX: 725.03,
    anchorY: 271.2,
    coreX: 648.26,
    coreY: 272.92,
  },
  {
    id: "files",
    label: "Files",
    iconSrc: "/os/assets/icons/file-explorer.svg",
    iconAlt: "File Explorer",
    chip: "workspace graph",
    x: 17.4,
    y: 80.73,
    anchorX: 306.52,
    anchorY: 511.25,
    coreX: 362.25,
    coreY: 354.96,
  },
  {
    id: "apps",
    label: "Apps",
    iconSrc: "/os/assets/icons/all-programs.svg",
    iconAlt: "All Programs",
    chip: "modular runtime",
    x: 84.07,
    y: 79.19,
    anchorX: 705.05,
    anchorY: 498.3,
    coreX: 637.75,
    coreY: 355.82,
  },
];

type HeroLayoutSnapshot = {
  core: typeof initialCore;
  nodes: Array<Pick<LayoutNode, "id" | "x" | "y" | "anchorX" | "anchorY" | "coreX" | "coreY">>;
};

declare global {
  interface Window {
    dumpHeroSystemLayout?: () => HeroLayoutSnapshot;
    copyHeroSystemLayout?: () => Promise<string>;
    enableHeroSystemLayoutEdit?: () => void;
    disableHeroSystemLayoutEdit?: () => void;
  }
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function round(value: number, precision = 2) {
  const multiplier = 10 ** precision;
  return Math.round(value * multiplier) / multiplier;
}

function buildPath(nodeId: HeroNodeId, fromX: number, fromY: number, toX: number, toY: number) {
  if (nodeId === "knowledge" || nodeId === "blueprints") {
    return `M ${fromX} ${fromY} L ${toX} ${toY}`;
  }

  const deltaX = toX - fromX;
  const controlOneX = fromX + deltaX * 0.3;
  const controlTwoX = fromX + deltaX * 0.72;

  return `M ${fromX} ${fromY} C ${controlOneX} ${fromY}, ${controlTwoX} ${toY}, ${toX} ${toY}`;
}

type NodeCardProps = {
  node: LayoutNode;
  isEditMode: boolean;
  onOpen: (id: HeroNodeId) => void;
  onStartDrag: (event: React.PointerEvent<HTMLElement>, id: HeroNodeId) => void;
};

function NodeCard({ node, isEditMode, onOpen, onStartDrag }: NodeCardProps) {
  const shouldReduceMotion = useReducedMotion();
  const isWideCard = node.id === "knowledge" || node.id === "blueprints";

  return (
    <motion.div
      className="absolute z-20 -translate-x-1/2 -translate-y-1/2"
      style={{ left: `${node.x}%`, top: `${node.y}%` }}
      whileHover={isEditMode || shouldReduceMotion ? undefined : { y: -3, scale: 1.015 }}
    >
      <div className="relative flex flex-col items-start select-none">
        {isEditMode && (
          <div className="absolute -top-3 right-3 z-20 rounded-full border border-primary/30 bg-black/80 px-2 py-1 text-[10px] font-medium uppercase tracking-[0.24em] text-primary/85 shadow-[0_0_18px_rgba(132,255,76,0.2)]">
            Drag node
          </div>
        )}

        <div
          role={isEditMode ? "presentation" : undefined}
          aria-label={isEditMode ? `Drag ${node.label} node` : undefined}
          onClick={() => {
            if (!isEditMode) {
              onOpen(node.id);
            }
          }}
          onPointerDown={(event) => {
            if (isEditMode) {
              onStartDrag(event, node.id);
            }
          }}
          className={cn(
            "flex touch-none items-center gap-2 rounded-[1.35rem] border border-primary/25 bg-[linear-gradient(180deg,rgba(10,18,12,0.97),rgba(7,13,9,0.97))] px-3 py-2.5 text-left shadow-[0_24px_60px_rgba(0,0,0,0.34)] backdrop-blur outline-none transition-all hover:border-primary/40 hover:shadow-[0_28px_70px_rgba(0,0,0,0.4)] sm:gap-3 sm:rounded-[1.5rem] sm:px-3.5 sm:py-3 lg:rounded-[1.6rem] lg:px-4 lg:py-3.5",
            isWideCard ? "w-[10.75rem] sm:w-[12.25rem] lg:w-[14rem]" : "w-[9.4rem] sm:w-[10.75rem] lg:w-[12.2rem]",
            isEditMode
              ? "cursor-grab active:cursor-grabbing"
              : "cursor-pointer focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#070b10]",
          )}
        >
          <img
            src={node.iconSrc}
            alt={node.iconAlt}
            className="h-6 w-6 shrink-0 object-contain sm:h-6 sm:w-6 lg:h-7 lg:w-7"
            loading="lazy"
          />
          <span className="min-w-0 flex-1 text-white">
            <span className="block truncate text-[0.9rem] font-semibold sm:text-[0.98rem] lg:text-[1.05rem]">{node.label}</span>
          </span>
          <Grip className={cn("h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4", isEditMode ? "text-primary/70" : "text-white/28")} />
        </div>

        <div className="ml-3.5 mt-2.5 rounded-full border border-primary/15 bg-primary/10 px-3 py-1.5 text-center text-[10px] text-primary/85 shadow-[0_0_24px_rgba(132,255,76,0.08)] sm:ml-4 sm:mt-3 sm:px-4 sm:py-1.5 sm:text-[10.5px] lg:ml-5 lg:mt-3.5 lg:px-5 lg:py-2 lg:text-[11px]">
          {node.chip}
        </div>
      </div>
    </motion.div>
  );
}

function RailDots({ index }: { index: number }) {
  return (
    <div className="flex items-center gap-2">
      {[0, 1, 2].map((dot) => {
        const timing = inputDotTimings[index] ?? { baseDelay: "0s", duration: "1.3s" };

        return (
          <span
            key={dot}
            className="maple-input-dot block h-3.5 w-3.5 rounded-full bg-slate-400/80"
            style={{
              animationDelay: `calc(${timing.baseDelay} + ${dot * 0.16}s)`,
              animationDuration: timing.duration,
            }}
          />
        );
      })}
    </div>
  );
}

type GraphStageProps = {
  core: typeof initialCore;
  nodes: LayoutNode[];
  isEditMode: boolean;
  onOpen: (id: HeroNodeId) => void;
  onUpdateNodePercent: (id: HeroNodeId, x: number, y: number) => void;
  onUpdateNodeAnchor: (id: HeroNodeId, x: number, y: number) => void;
  onUpdateNodeCore: (id: HeroNodeId, x: number, y: number) => void;
  onUpdateCoreCenter: (x: number, y: number) => void;
  shouldReduceMotion: boolean;
  showConnections: boolean;
};

type DragState =
  | { kind: "node"; nodeId: HeroNodeId }
  | { kind: "anchor"; nodeId: HeroNodeId }
  | { kind: "branch-core"; nodeId: HeroNodeId }
  | { kind: "core-center" }
  | null;

function GraphStage({
  core,
  nodes,
  isEditMode,
  onOpen,
  onUpdateNodePercent,
  onUpdateNodeAnchor,
  onUpdateNodeCore,
  onUpdateCoreCenter,
  shouldReduceMotion,
  showConnections,
}: GraphStageProps) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const centerPanelRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [dragState, setDragState] = useState<DragState>(null);

  useEffect(() => {
    if (!isEditMode && dragState) {
      setDragState(null);
    }
  }, [dragState, isEditMode]);

  const updateFromPointer = (clientX: number, clientY: number) => {
    if (!dragState) {
      return;
    }

    if (dragState.kind === "node") {
      const rect = centerPanelRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      const nextX = clamp(((clientX - rect.left) / rect.width) * 100, 4, 96);
      const nextY = clamp(((clientY - rect.top) / rect.height) * 100, 8, 92);
      onUpdateNodePercent(dragState.nodeId, round(nextX), round(nextY));
      return;
    }

    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }

    const nextX = clamp(((clientX - rect.left) / rect.width) * SVG_WIDTH, 0, SVG_WIDTH);
    const nextY = clamp(((clientY - rect.top) / rect.height) * SVG_HEIGHT, 0, SVG_HEIGHT);

    if (dragState.kind === "anchor") {
      onUpdateNodeAnchor(dragState.nodeId, round(nextX), round(nextY));
      return;
    }

    if (dragState.kind === "branch-core") {
      onUpdateNodeCore(dragState.nodeId, round(nextX), round(nextY));
      return;
    }

    onUpdateCoreCenter(round(nextX), round(nextY));
  };

  const startDrag = (event: React.PointerEvent<HTMLElement>, nextDragState: Exclude<DragState, null>) => {
    if (!isEditMode) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setDragState(nextDragState);
    updateFromPointer(event.clientX, event.clientY);
  };

  const stopDrag = () => {
    if (dragState) {
      setDragState(null);
    }
  };

  return (
    <div
      ref={stageRef}
      className="relative h-[28rem] min-w-[640px] touch-none overflow-hidden rounded-[1.75rem] border border-white/8 bg-black/25 sm:h-[32rem] sm:min-w-[760px] sm:rounded-[1.95rem] xl:h-[39rem] xl:min-w-[1180px] xl:rounded-[2.15rem]"
      onPointerMove={(event) => updateFromPointer(event.clientX, event.clientY)}
      onPointerUp={stopDrag}
      onPointerCancel={stopDrag}
      onPointerLeave={stopDrag}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(132,255,76,0.08),transparent_32%)]" />

      <div className="relative grid h-full min-w-[640px] grid-cols-1 sm:min-w-[760px] xl:min-w-[1180px] xl:grid-cols-[250px_minmax(0,1fr)_250px]">
        <div className="relative z-20 hidden h-full flex-col justify-center px-8 py-8 xl:flex">
          <div className="mb-8 text-[11px] uppercase tracking-[0.34em] text-white/35">Context inputs</div>
          <div className="space-y-8">
            {leftRail.map((item, index) => (
              <div key={item} className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center gap-3 text-[1.05rem] text-white/75">
                <RailDots index={index} />
                <span className="whitespace-nowrap text-left">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div ref={centerPanelRef} className="relative min-w-0 px-3 py-4 sm:px-4 sm:py-5 xl:px-0 xl:py-6">
          <div className="relative h-full rounded-[1.7rem] sm:rounded-[1.85rem] lg:rounded-[2rem]">
            <div className="pointer-events-none absolute inset-0 rounded-[1.7rem] bg-[radial-gradient(circle_at_center,rgba(132,255,76,0.04),transparent_60%)] sm:rounded-[1.85rem] lg:rounded-[2rem]" />

            {showConnections ? (
              <svg ref={svgRef} viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`} className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
                <ellipse cx={core.centerX} cy={core.centerY} rx="252" ry="176" fill="none" stroke="rgba(138,163,255,0.12)" strokeWidth="1.5" />
                <ellipse cx={core.centerX} cy={core.centerY} rx="334" ry="234" fill="none" stroke="rgba(138,163,255,0.1)" strokeWidth="1.5" />

                {nodes.map((node) => {
                  const path = buildPath(node.id, node.coreX, node.coreY, node.anchorX, node.anchorY);

                  return (
                    <g key={node.id}>
                      <motion.path
                        d={path}
                        fill="none"
                        stroke="rgba(132,255,76,0.32)"
                        strokeWidth="3.25"
                        strokeLinecap="round"
                        animate={shouldReduceMotion ? undefined : { opacity: [0.72, 1, 0.72] }}
                        transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                      />
                      <path
                        d={path}
                        fill="none"
                        stroke="rgba(132,255,76,0.12)"
                        strokeWidth="6"
                        strokeLinecap="round"
                      />
                      <circle cx={node.anchorX} cy={node.anchorY} r="6.5" fill="rgba(132,255,76,0.98)" />
                      <circle cx={node.coreX} cy={node.coreY} r="5.5" fill="rgba(132,255,76,0.9)" />
                    </g>
                  );
                })}
              </svg>
            ) : null}

            <motion.div
              animate={shouldReduceMotion ? undefined : { scale: [1, 1.03, 1], opacity: [0.88, 1, 0.88] }}
              transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut" }}
              onPointerDown={(event) => startDrag(event, { kind: "core-center" })}
              className={cn(
                "absolute left-1/2 z-20 w-[10.5rem] touch-none -translate-x-1/2 -translate-y-1/2 rounded-[1.45rem] border border-primary/25 bg-[linear-gradient(180deg,rgba(11,22,13,0.98),rgba(7,14,9,0.98))] px-3.5 py-3.5 text-center shadow-[0_0_70px_rgba(132,255,76,0.14)] backdrop-blur sm:w-[12rem] sm:rounded-[1.65rem] sm:px-4 sm:py-4 lg:w-[13.5rem] lg:rounded-[1.85rem] lg:px-5 lg:py-5",
                isEditMode ? "cursor-grab active:cursor-grabbing" : "pointer-events-none",
              )}
              style={{
                left: `${(core.centerX / SVG_WIDTH) * 100}%`,
                top: `${(core.centerY / SVG_HEIGHT) * 100}%`,
              }}
            >
              <div className="text-[9px] uppercase tracking-[0.28em] text-primary/80 sm:text-[10px] sm:tracking-[0.32em] lg:text-[11px] lg:tracking-[0.34em]">Core</div>
              <MapleOSLogo
                textOnly
                className="mx-auto mt-3 h-auto w-[6.6rem] max-w-full text-white sm:mt-3.5 sm:w-[7.7rem] lg:mt-4 lg:w-[8.9rem]"
                textClassName="fill-white"
                aria-hidden="true"
              />
              <div className="mt-3 text-[0.82rem] leading-6 text-white/58 sm:text-[0.92rem] sm:leading-6 lg:mt-4 lg:text-[1rem] lg:leading-7">Living System</div>
            </motion.div>

            {nodes.map((node) => (
              <NodeCard
                key={node.id}
                node={node}
                isEditMode={isEditMode}
                onOpen={onOpen}
                onStartDrag={(event, id) => startDrag(event, { kind: "node", nodeId: id })}
              />
            ))}

            {isEditMode && (
              <>
                {nodes.map((node) => (
                  <React.Fragment key={`${node.id}-handles`}>
                    <button
                      type="button"
                      aria-label={`Drag ${node.label} card anchor`}
                      title={`${node.label} anchor`}
                      onPointerDown={(event) => startDrag(event, { kind: "anchor", nodeId: node.id })}
                      className="absolute z-30 h-4 w-4 touch-none -translate-x-1/2 -translate-y-1/2 rounded-full border border-sky-200/60 bg-sky-400 shadow-[0_0_18px_rgba(56,189,248,0.7)] cursor-grab active:cursor-grabbing"
                      style={{
                        left: `${(node.anchorX / SVG_WIDTH) * 100}%`,
                        top: `${(node.anchorY / SVG_HEIGHT) * 100}%`,
                      }}
                    />
                    <button
                      type="button"
                      aria-label={`Drag ${node.label} branch core point`}
                      title={`${node.label} branch core point`}
                      onPointerDown={(event) => startDrag(event, { kind: "branch-core", nodeId: node.id })}
                      className="absolute z-30 h-4 w-4 touch-none -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary/60 bg-primary shadow-[0_0_18px_rgba(132,255,76,0.65)] cursor-grab active:cursor-grabbing"
                      style={{
                        left: `${(node.coreX / SVG_WIDTH) * 100}%`,
                        top: `${(node.coreY / SVG_HEIGHT) * 100}%`,
                      }}
                    />
                  </React.Fragment>
                ))}

                <button
                  type="button"
                  aria-label="Drag MapleOS core center"
                  title="Core center"
                  onPointerDown={(event) => startDrag(event, { kind: "core-center" })}
                  className="absolute z-30 h-4 w-4 touch-none -translate-x-1/2 -translate-y-1/2 rounded-full border border-amber-200/60 bg-amber-400 shadow-[0_0_18px_rgba(251,191,36,0.65)] cursor-grab active:cursor-grabbing"
                  style={{
                    left: `${(core.centerX / SVG_WIDTH) * 100}%`,
                    top: `${(core.centerY / SVG_HEIGHT) * 100}%`,
                  }}
                />

                <div className="pointer-events-none absolute bottom-5 left-5 z-30 rounded-2xl border border-white/10 bg-black/60 px-4 py-3 text-left text-[11px] text-white/70 backdrop-blur">
                  <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/45">
                    <Move className="h-3.5 w-3.5" />
                    Layout edit mode
                  </div>
                  <div className="space-y-1.5">
                    <div><span className="text-sky-300">Blue dots</span> = card anchors</div>
                    <div><span className="text-primary">Green dots</span> = branch core points</div>
                    <div><span className="text-amber-300">Amber dot</span> = MapleOS center</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="relative z-20 hidden h-full flex-col justify-center px-8 py-8 xl:flex">
          <div className="mb-8 text-[11px] uppercase tracking-[0.34em] text-white/35">System outputs</div>
          <div className="space-y-8">
            {rightRail.map((item, index) => (
              <div key={item} className="flex items-center gap-4 text-[1.05rem] text-white/75">
                <span
                  className="maple-output-dot h-4 w-4 rounded-full bg-primary shadow-[0_0_18px_rgba(132,255,76,0.55)]"
                  style={{ animationDelay: `${index * 0.7}s` }}
                />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HeroSystemPreview({ className }: { className?: string }) {
  const shouldReduceMotion = useReducedMotion();
  const [selectedNodeId, setSelectedNodeId] = useState<HeroNodeId | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [core, setCore] = useState(initialCore);
  const [nodes, setNodes] = useState<LayoutNode[]>(() => initialNodes.map((node) => ({ ...node })));
  const [layoutMode, setLayoutMode] = useState<"desktop" | "medium" | "mobile">("desktop");

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const updateLayoutMode = () => {
      if (window.innerWidth < 768) {
        setLayoutMode("mobile");
        return;
      }

      if (window.innerWidth < 1280) {
        setLayoutMode("medium");
        return;
      }

      setLayoutMode("desktop");
    };

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);

    return () => {
      window.removeEventListener("resize", updateLayoutMode);
    };
  }, []);

  const displayCore = useMemo(() => {
    if (isEditMode) {
      return core;
    }

    if (layoutMode === "mobile") {
      return mobileCore;
    }

    if (layoutMode === "medium") {
      return compactCore;
    }

    return core;
  }, [core, isEditMode, layoutMode]);

  const displayNodes = useMemo(() => {
    if (isEditMode) {
      return nodes;
    }

    if (layoutMode === "mobile") {
      return nodes.map((node) => ({
        ...node,
        ...mobileNodeLayout[node.id],
      }));
    }

    if (layoutMode === "medium") {
      return nodes.map((node) => ({
        ...node,
        ...mediumNodeLayout[node.id],
      }));
    }

    return nodes;
  }, [isEditMode, layoutMode, nodes]);

  const showConnections = layoutMode === "desktop" || isEditMode;

  const layoutSnapshot = useMemo<HeroLayoutSnapshot>(
    () => ({
      core: {
        centerX: round(core.centerX),
        centerY: round(core.centerY),
      },
      nodes: nodes.map((node) => ({
        id: node.id,
        x: round(node.x),
        y: round(node.y),
        anchorX: round(node.anchorX),
        anchorY: round(node.anchorY),
        coreX: round(node.coreX),
        coreY: round(node.coreY),
      })),
    }),
    [core, nodes],
  );

  useEffect(() => {
    window.dumpHeroSystemLayout = () => {
      console.log("heroSystemLayout", layoutSnapshot);
      console.log(JSON.stringify(layoutSnapshot, null, 2));
      return layoutSnapshot;
    };

    window.copyHeroSystemLayout = async () => {
      const serialized = JSON.stringify(layoutSnapshot, null, 2);
      await navigator.clipboard.writeText(serialized);
      console.log("Copied hero system layout JSON to clipboard.");
      return serialized;
    };

    window.enableHeroSystemLayoutEdit = () => {
      setIsEditMode(true);
    };

    window.disableHeroSystemLayoutEdit = () => {
      setIsEditMode(false);
    };

    return () => {
      delete window.dumpHeroSystemLayout;
      delete window.copyHeroSystemLayout;
      delete window.enableHeroSystemLayoutEdit;
      delete window.disableHeroSystemLayoutEdit;
    };
  }, [layoutSnapshot]);

  const updateNode = (nodeId: HeroNodeId, updater: (node: LayoutNode) => LayoutNode) => {
    setNodes((currentNodes) => currentNodes.map((node) => (node.id === nodeId ? updater(node) : node)));
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.12 }}
        className={cn("relative mx-auto w-full max-w-[92rem]", className)}
      >
        <div className="pointer-events-none absolute inset-x-[16%] top-4 h-40 rounded-full bg-primary/12 blur-3xl" />

        <div className="relative overflow-hidden rounded-[1.7rem] border border-white/10 bg-[linear-gradient(180deg,rgba(8,12,17,0.98),rgba(6,10,16,0.98))] shadow-[0_40px_140px_rgba(0,0,0,0.55)] sm:rounded-[2rem] lg:rounded-[2.2rem]">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(132,255,76,0.14),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.08),transparent_30%)]" />
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:28px_28px] opacity-20" />

          <div className="relative border-b border-white/10 px-4 py-3 sm:px-6 sm:py-4 lg:px-7">
            <div className="flex items-center justify-between gap-3 text-[9px] uppercase tracking-[0.26em] text-white/45 sm:text-[10px] sm:tracking-[0.3em] lg:text-[11px] lg:tracking-[0.32em]">
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="h-2.5 w-2.5 rounded-full bg-primary/90 sm:h-3 sm:w-3" />
                MapleOS ecosystem online
              </div>
              <span className="text-[8px] normal-case tracking-normal text-white/38 sm:hidden">Swipe</span>
            </div>
          </div>

          <div className="relative overflow-x-auto px-2 py-2 sm:px-4 sm:py-4 lg:px-6 lg:py-6">
            <GraphStage
              core={displayCore}
              nodes={displayNodes}
              isEditMode={isEditMode}
              onOpen={setSelectedNodeId}
              onUpdateNodePercent={(id, x, y) => {
                updateNode(id, (node) => ({ ...node, x, y }));
              }}
              onUpdateNodeAnchor={(id, x, y) => {
                updateNode(id, (node) => ({ ...node, anchorX: x, anchorY: y }));
              }}
              onUpdateNodeCore={(id, x, y) => {
                updateNode(id, (node) => ({ ...node, coreX: x, coreY: y }));
              }}
              onUpdateCoreCenter={(x, y) => {
                setCore({ centerX: x, centerY: y });
              }}
              shouldReduceMotion={shouldReduceMotion}
              showConnections={showConnections}
            />
          </div>
        </div>

        <p id={transcriptId} className="sr-only">
          MapleOS system graph preview with MapleOS at the center, connected node cards for chat, terminal, blueprints, files, apps, and knowledge, plus surrounding context inputs and system outputs.
        </p>
      </motion.div>

      <HeroNodeDetailsDialog
        nodeId={selectedNodeId}
        open={selectedNodeId !== null && !isEditMode}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedNodeId(null);
          }
        }}
      />
    </>
  );
}